import React from 'react';
import { css } from '@emotion/css';
import TemplateFormFieldModel from '../../../models/templating/api/TemplateFormFieldModel';
import { color_shades_dark, color_text_light } from '../../../constants/colors';
import ToggleIndicator from '../../../components/general/ToggleIndicator';
import TextField from '../../../components/forms/TextField';
import HorizontalSeparator from '../../../components/structure/HorizontalSeparator';
import Switch from '../../../components/forms/Switch';
import MarkdownEditorModal from './MarkdownEditorModal';
import LargeOverflowMenu from '../../../components/general/LargeOverflowMenu';
import ConfirmationDialog from '../../../components/structure/ConfirmationDialog';
import DeleteConfirmation from '../../featureContent/components/DeleteConfirmation';
import TemplateFormSectionModel from '../../../models/templating/api/TemplateFormSectionModel';
import { OverflowOption } from '../../../components/general/overflowMenu/OverflowMenu';
import { DraggableProvided } from 'react-beautiful-dnd';
import TemplateFieldType from '../../../models/templating/TemplateFieldType';
import { getFieldTypeIcon } from '../../../models/extensions/fieldTypeIcons';
import ConditionalDefaultEditor from './ConditionalDefaultEditor';
import { ConditionalDefault } from '../../../models/templating/api/ConditionalDefaultModel';
import FieldTypeChangeSelector from './FieldTypeChangeSelector';

const menuIcon = require('../../../content/images/menu-item-icons/circle-menu.svg');
const deleteIcon = require('../../../content/images/menu-item-icons/bin.svg');
const upIcon = require('../../../content/images/menu-item-icons/navigation-up.svg');
const downIcon = require('../../../content/images/menu-item-icons/navigation-down.svg');
const moveIcon = require('../../../content/images/menu-item-icons/navigation-right-circle.svg');

interface TimeRangeFieldEditorProps {
    field: TemplateFormFieldModel
    sections: TemplateFormSectionModel[]
    allFields: TemplateFormFieldModel[]
    draggable?: DraggableProvided
    onChange: (field: TemplateFormFieldModel) => void
    onDelete: (field: TemplateFormFieldModel) => void
    onMoveUp: (field: TemplateFormFieldModel) => void
    onMoveDown: (field: TemplateFormFieldModel) => void
    onMoveToSection: (field: TemplateFormFieldModel, sectionId: string) => void
    onCopyConditionalDefaultToField?: (sourceFieldId: string, targetFieldId: string) => void
    defaultExpanded?: boolean
}

interface TimeRangeFieldEditorState {
    isCollapsed: boolean
    isMarkdownOpen: boolean
    isDeleteConfirmationOpen: boolean
}

class TimeRangeFieldEditor extends React.Component<TimeRangeFieldEditorProps, TimeRangeFieldEditorState> {
    constructor(props) {
        super(props);
        this.state = {
            isCollapsed: !props.defaultExpanded,
            isMarkdownOpen: false,
            isDeleteConfirmationOpen: false
        }
    }
    handleToggleCollapse() {
        this.setState({
            ...this.state,
            isCollapsed: !this.state.isCollapsed
        })
    }
    handleToggleMarkdown() {
        this.setState({
            ...this.state,
            isMarkdownOpen: !this.state.isMarkdownOpen
        })
    }
    handleToggleDelete() {
        this.setState({
            ...this.state,
            isDeleteConfirmationOpen: !this.state.isDeleteConfirmationOpen
        })
    }
    handleMarkdownChange = (markdown: string) => {
        this.handleFieldChange("instructionsMarkdown", markdown);
        this.setState({
            ...this.state,
            isMarkdownOpen: false
        })
    }
    handleFieldChange(fieldName: keyof TemplateFormFieldModel, value) {
        const updatedField = {
            ...this.props.field,
            [fieldName]: value
        };
        this.props.onChange(updatedField);
    }
    handleConditionalDefaultChange = (conditionalDefaults: ConditionalDefault | undefined) => {
        this.props.onChange({ ...this.props.field, conditionalDefaults });
    }
    handleFieldTypeChange = (updatedField: TemplateFormFieldModel) => {
        this.props.onChange(updatedField);
        this.setState({ isCollapsed: false });
    }
    getOptions() {
        const options: OverflowOption[] = [
            { label: 'Delete', icon: deleteIcon, hoverIcon: deleteIcon, isDestructive: true },
            { label: 'Move Up', icon: upIcon, hoverIcon: upIcon },
            { label: 'Move Down', icon: downIcon, hoverIcon: downIcon },
            {
                label: 'Move to another page...',
                icon: moveIcon, hoverIcon: moveIcon,
                subOptions: this.props.sections.map(s => ({
                    label: s.title
                }))
            }
        ];
        return options;
    }
    handleOptionSelected(optionName: string) {
        if (optionName == 'Move Up') {
            this.props.onMoveUp(this.props.field);
        }
        if (optionName == 'Move Down') {
            this.props.onMoveDown(this.props.field);
        }
        if (optionName == 'Delete') {
            this.setState({
                ...this.state,
                isDeleteConfirmationOpen: true
            })
        }

        // find section by name and pass it off
        const section = this.props.sections.find(s => s.title == optionName);
        if (section) {
            this.props.onMoveToSection(this.props.field, section.id);
        }
    }
    render() {
        const { field, draggable } = this.props;
        return (
            <div id={field.id} className={containerStyle}>
                <div className="field-title-wrapper" {...(draggable?.dragHandleProps ?? {})}>
                    <ToggleIndicator direction={this.state.isCollapsed ? 'right' : 'down'} onClick={this.handleToggleCollapse.bind(this)} type="triangle" className="toggle-indicator" />
                    <img src={getFieldTypeIcon(field.fieldType as TemplateFieldType)} onClick={this.handleToggleCollapse.bind(this)} />
                    <div className="title-text-container">
                        <FieldTypeChangeSelector field={field} onChange={this.handleFieldTypeChange} onExpand={() => this.setState({ isCollapsed: false })} />
                        <p className="field-title" onClick={this.handleToggleCollapse.bind(this)}>{field.title}</p>
                    </div>
                    <div className="field-button">
                        <LargeOverflowMenu
                            isActive={false}
                            onOptionSelected={this.handleOptionSelected.bind(this)}
                            options={this.getOptions()}
                            icon={menuIcon}
                        />
                    </div>
                </div>
                {!this.state.isCollapsed && <div className="field-body-wrapper">
                    <TextField name="field.associatedVariable" value={field.associatedVariable ?? ''} placeholder="{Variable}" label="Associated Variable" onChange={e => this.handleFieldChange("associatedVariable", e.target.value)} />
                    <HorizontalSeparator />
                    <Switch className="switch-container" checked={field.isRequired} onChange={(checked) => this.handleFieldChange("isRequired", checked)} label="Required Field" />
                    <TextField name="field.title" value={field.title ?? ''} placeholder="Title your field" label="Display Title" onChange={e => this.handleFieldChange("title", e.target.value)} />
                    <TextField name="field.label" value={field.label ?? ''} placeholder="Label for weekly hours" label="Field Label" onChange={e => this.handleFieldChange("label", e.target.value)} />
                    <TextField name="field.description" value={field.tip ?? ''} placeholder="Describe your field (e.g., 'Set your business hours for each day')" label="Description of Field" onChange={e => this.handleFieldChange("tip", e.target.value)} />
                    <div className="info-section">
                        <p className="info-label">Backend Format:</p>
                        <p className="info-text">Data is sent as an array of 7 strings (Mon-Sun) with CLOSED hours (not open hours) in 24-hour format. Example: "17:00,09:00" means closed from 5pm-9am. Use pipe (|) for multiple closed periods: "00:00,09:00|17:00,23:59". Closed all day: "00:00,23:59".</p>
                    </div>
                    <ConditionalDefaultEditor
                        allFields={this.props.allFields}
                        currentField={field}
                        value={field.conditionalDefaults}
                        onChange={this.handleConditionalDefaultChange}
                        onCopyTo={this.props.onCopyConditionalDefaultToField
                            ? (targetFieldId) => this.props.onCopyConditionalDefaultToField(this.props.field.id, targetFieldId)
                            : undefined}
                    />
                </div>}
                {this.state.isMarkdownOpen && <MarkdownEditorModal contextTitle={field.title ?? "Field Instructions"} markdown={this.props.field.instructionsMarkdown ?? ''} onSave={this.handleMarkdownChange.bind(this)} onClose={this.handleToggleMarkdown.bind(this)} />}
                {this.state.isDeleteConfirmationOpen &&
                    <ConfirmationDialog title={`Delete ${field?.title ?? "Field"}`}
                        deleteText="Yes, Delete"
                        isLoading={false}
                        onClose={this.handleToggleDelete.bind(this)}
                        onConfirm={() => this.props.onDelete(this.props.field)}>
                        <DeleteConfirmation itemLabel={`${field?.title ?? "Field"}?`} />
                    </ConfirmationDialog>
                }
            </div>
        );
    }
}

const containerStyle = css`
    border: 1px solid ${color_shades_dark};
    background: white;
    border-radius: 8px;
    margin-bottom: 32px;
    .field-title-wrapper {
        display: flex;
        align-items: center;
        margin: 16px 0;
        cursor: pointer;
        >.toggle-indicator {
            margin: 16px;
            transform: scale(2);
        }
        >img {
            width: 24px;
            height: auto;
            margin-right: 12px;
        }
        .title-text-container {
            flex: 1;
        }
        .field-type-label {
            font-style: normal;
            font-weight: normal;
            font-size: 12px;
            line-height: 16px;
            color: ${color_text_light};
        }
        .field-title {
            font-style: normal;
            font-weight: normal;
            font-size: 18px;
            line-height: 24px;
        }
        .field-button {
            margin-right: 32px;
            margin-left: auto;
        }
    }
    .field-body-wrapper {
        margin: 0 32px;
        padding: 24px 0;
        border-top: 1px solid ${color_shades_dark};
        >.switch-container {
            margin: 32px 0;
        }
        .info-section {
            margin-top: 24px;
            padding: 16px;
            background: #f8f9fa;
            border-radius: 4px;
            border-left: 3px solid ${color_shades_dark};
            .info-label {
                font-weight: 600;
                font-size: 12px;
                color: ${color_text_light};
                margin-bottom: 8px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }
            .info-text {
                font-size: 13px;
                line-height: 1.5;
                color: ${color_text_light};
                margin: 0;
            }
        }
    }
`;

export default TimeRangeFieldEditor;
